import React from 'react';
import {
  Button,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
} from '@patternfly/react-core';
import { ExternalLinkAltIcon } from '@patternfly/react-icons';
import type { PaymentAttribution } from '../../../types/models/Reconciliation';
import { facturierInvoiceUrl } from '../../../utils/facturier';
import {
  DOMAIN_ORDER_STATUS_LABELS,
  DOMAIN_ORDER_TYPE_LABELS,
  MATCHED_BY_LABELS,
  SOURCE_LABELS,
  labelOf,
} from './reconciliationLabels';

export interface DetailItem {
  term: string;
  value: React.ReactNode;
}

/** A compact two-column list of the non-empty details (null / empty values are left out). */
export const DetailList: React.FunctionComponent<{ items: DetailItem[] }> = ({ items }) => {
  const shown = items.filter((i) => i.value !== null && i.value !== undefined && i.value !== '');
  return (
    <DescriptionList isCompact isHorizontal columnModifier={{ default: '1Col', lg: '2Col' }}>
      {shown.map((i) => (
        <DescriptionListGroup key={i.term}>
          <DescriptionListTerm>{i.term}</DescriptionListTerm>
          <DescriptionListDescription>{i.value}</DescriptionListDescription>
        </DescriptionListGroup>
      ))}
    </DescriptionList>
  );
};

const idOrNull = (prefix: string, id: number | null) => (id === null ? null : `${prefix}${id}`);

/** A Facturier invoice by its numero (its id while the numero is unknown), linked to its Naugrix page. */
export const FacturierInvoiceLink: React.FunctionComponent<{ id: number; numero: string | null }> = ({
  id,
  numero,
}) => {
  const label = numero ?? `ID ${id}`;
  const href = facturierInvoiceUrl(id);
  return href === null ? (
    <>{label}</>
  ) : (
    <Button
      variant="link"
      isInline
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      icon={<ExternalLinkAltIcon />}
      iconPosition="end"
    >
      {label}
    </Button>
  );
};

/** How a Mollie payment was attributed to the Manager (journal rows and payout lines). */
export function attributionItems(r: PaymentAttribution): DetailItem[] {
  const order =
    r.domain_order_id === null
      ? null
      : [
          `#${r.domain_order_id}`,
          r.domain_order_type === null ? null : labelOf(DOMAIN_ORDER_TYPE_LABELS, r.domain_order_type),
          r.domain_order_status === null ? null : labelOf(DOMAIN_ORDER_STATUS_LABELS, r.domain_order_status),
        ]
          .filter(Boolean)
          .join(' · ');
  return [
    { term: 'Source', value: r.source === null ? null : labelOf(SOURCE_LABELS, r.source) },
    { term: 'Organisation Manager', value: idOrNull('#', r.client_id) },
    { term: 'Clé comptable', value: r.accounting_key },
    {
      term: 'Facture Facturier',
      value:
        r.facturier_invoice_id === null ? (
          r.invoice_numero
        ) : (
          <FacturierInvoiceLink id={r.facturier_invoice_id} numero={r.invoice_numero} />
        ),
    },
    { term: 'Tentative de paiement', value: idOrNull('#', r.invoice_payment_id) },
    { term: 'Commande de domaine', value: order },
    { term: 'Domaines', value: r.domains.length > 0 ? r.domains.join(', ') : null },
    { term: 'Rechargement', value: idOrNull('#', r.wallet_deposit_id) },
    { term: 'Enregistrement de carte', value: idOrNull('#', r.payment_mandate_id) },
    { term: 'Rapproché par', value: r.matched_by === null ? null : labelOf(MATCHED_BY_LABELS, r.matched_by) },
  ];
}
